import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
const {
  FiShield,
  FiLock,
  FiCpu,
  FiActivity,
  FiDatabase,
  FiArrowRight,
  FiAlertTriangle,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiTerminal,
  FiFileText,
  FiGlobe,
  FiAlertOctagon,
  FiKey,
  FiClock,
  FiUser,
  FiZap,
} = FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import supabase from "../supabase/supabase";
import toast from "react-hot-toast";
import { FEATURE_FLAGS } from "../lib/featureFlags";
import { format } from "date-fns";
import Fido2Enrollment from "../components/Fido2Enrollment";

export default function FounderPanel() {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("registry");
  const [auditLogs, setAuditLogs] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const { user } = useAuth();

  // Founder Login Logic
  const handleAuthorize = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/founder/verify", { secret });
      if (data.authorized) {
        setIsAuthorized(true);
        toast.success("Founder identity verified. Session audited.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid Founder credentials");
    } finally {
      setLoading(false);
    }
  };

  // Real-time Audit Stream Implementation
  useEffect(() => {
    if (!isAuthorized || activeTab !== "compliance") return;

    const fetchLogs = async () => {
      const { data, error } = await supabase
        .from("registry_audit_logs_2024")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) console.error("Audit Fetch Error:", error);
      else setAuditLogs(data);
    };

    fetchLogs();

    // Subscribe to real-time updates
    const channel = supabase
      .channel("registry_audit_stream")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "registry_audit_logs_2024",
        },
        (payload) => {
          setAuditLogs((prev) => [payload.new, ...prev].slice(0, 50));
          toast("New Audit Entry Detected", { icon: "🛡️" });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthorized, activeTab]);

  // Cohort Registry Logic
  useEffect(() => {
    if (!isAuthorized || activeTab !== "registry") return;

    const fetchCohorts = async () => {
      const { data, error } = await supabase
        .from("cohort_registry_2024")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) console.error("Cohort Fetch Error:", error);
      else setCohorts(data);
    };

    fetchCohorts();
  }, [isAuthorized, activeTab]);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-red-500/10 blur-[150px] rounded-full" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-primary-500/10 blur-[150px] rounded-full" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-[480px] w-full relative z-10"
        >
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-900 border border-slate-800 rounded-3xl mb-6 shadow-2xl relative group">
              <div className="absolute inset-0 bg-red-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <SafeIcon
                icon={FiKey}
                className="text-red-500 text-4xl relative z-10"
              />
            </div>
            <h1 className="text-3xl font-black text-white tracking-tight mb-2 uppercase italic">
              Founder Portal
            </h1>
            <div className="flex items-center justify-center gap-2">
              <span className="h-px w-8 bg-red-900/50" />
              <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.3em]">
                Tier 0 Access Only
              </span>
              <span className="h-px w-8 bg-red-900/50" />
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-800/50 backdrop-blur-3xl rounded-[2.5rem] p-10 shadow-2xl relative">
            <div className="absolute top-4 right-8 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[9px] font-bold text-red-500/70 uppercase tracking-widest">
                Audited Session
              </span>
            </div>

            <form onSubmit={handleAuthorize} className="space-y-8">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                  Foundation Secret
                </label>
                <div className="relative group">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-red-500 transition-colors">
                    <SafeIcon icon={FiTerminal} />
                  </div>
                  <input
                    type={showSecret ? "text" : "password"}
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    required
                    autoFocus
                    placeholder="ENTER AUTHORIZATION STRING"
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-14 pr-14 py-5 text-white focus:border-red-500/50 focus:ring-4 focus:ring-red-500/5 transition-all outline-none placeholder:text-slate-800 font-mono text-lg tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                  >
                    <SafeIcon icon={showSecret ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black py-5 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl shadow-red-900/20 active:scale-[0.98] disabled:opacity-50 group overflow-hidden relative"
              >
                <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Decrypt & Authorize
                    <SafeIcon
                      icon={FiArrowRight}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </>
                )}
              </button>
            </form>

            <div className="mt-10 p-4 bg-red-500/5 border border-red-500/10 rounded-2xl flex gap-4">
              <SafeIcon
                icon={FiAlertOctagon}
                className="text-red-500 text-xl flex-shrink-0"
              />
              <p className="text-[10px] text-slate-400 leading-relaxed">
                <span className="text-red-500 font-black">WARNING:</span> This
                portal is for primary founders only. All login attempts, IP
                addresses, and session activities are recorded in the immutable
                audit ledger.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center">
            <a
              href=""
              className="text-slate-500 hover:text-slate-300 text-[10px] font-black uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiShield} />
              Return to Standard Sign-In
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white p-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-12 border-b border-slate-800 pb-8">
          <div>
            <h1 className="text-4xl font-black italic tracking-tighter">
              FOUNDER CORE
            </h1>
            <p className="text-slate-500 font-mono text-xs uppercase tracking-widest mt-1">
              Institutional Governance & Compliance Engine
            </p>
          </div>
          <div className="flex gap-4">
            <div className="bg-slate-900 px-4 py-2 rounded-xl border border-slate-800 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-[10px] font-black uppercase tracking-widest">
                Protocol 4422 Active
              </span>
            </div>
            <button
              onClick={() => setIsAuthorized(false)}
              className="bg-red-500/10 text-red-500 border border-red-500/20 px-6 py-2 rounded-xl text-xs font-black uppercase hover:bg-red-500 hover:text-white transition-all"
            >
              Terminate Session
            </button>
          </div>
        </header>

        <div className="flex gap-4 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { id: "registry", label: "Cohort Registry", icon: FiDatabase },
            { id: "dsar", label: "DSAR Suite", icon: FiActivity },
            { id: "compliance", label: "Compliance Audit", icon: FiFileText },
            { id: "fido2", label: "Hardware Identity", icon: FiShield },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-primary-600 text-white shadow-lg shadow-primary-900/20"
                  : "bg-slate-900 text-slate-500 hover:bg-slate-800"
              }`}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-8 min-h-[600px] backdrop-blur-3xl">
          {activeTab === "registry" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-black uppercase italic tracking-widest">
                  Cohort Registry
                </h2>
                <button className="bg-primary-600 hover:bg-primary-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">
                  Initialize Cohort
                </button>
              </div>
              <div className="overflow-hidden border border-slate-800 rounded-2xl">
                <table className="w-full text-left">
                  <thead className="bg-slate-950/50 border-b border-slate-800">
                    <tr>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Cohort ID
                      </th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Institution
                      </th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Status
                      </th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {cohorts.map((cohort) => (
                      <tr
                        key={cohort.id}
                        className="hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="px-6 py-4 font-mono text-sm text-primary-400">
                          {cohort.cohort_id}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300">
                          {cohort.institution_name}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${cohort.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-500"}`}
                          >
                            {cohort.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500 font-mono">
                          {format(
                            new Date(cohort.created_at),
                            "yyyy-MM-dd HH:mm",
                          )}
                        </td>
                      </tr>
                    ))}
                    {cohorts.length === 0 && (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-20 text-center text-slate-600 italic"
                        >
                          No institutional cohorts registered.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "compliance" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-black uppercase italic tracking-widest">
                  Audit Stream
                </h2>
                <div className="flex items-center gap-2 text-primary-500 text-[10px] font-black uppercase tracking-[0.2em] animate-pulse">
                  <SafeIcon icon={FiZap} />
                  Live Feed Active
                </div>
              </div>
              <div className="space-y-3">
                {auditLogs.map((log) => (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    key={log.id}
                    className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex items-center gap-6 group hover:border-primary-500/30 transition-all"
                  >
                    <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-primary-500 transition-colors">
                      <SafeIcon icon={FiFileText} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="text-xs font-black uppercase tracking-widest text-white">
                          {log.action}
                        </p>
                        <span className="text-[9px] text-slate-600 font-mono">
                          {log.id.split("-")[0]}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1 font-mono">
                        Cohort:{" "}
                        <span className="text-primary-500/80">
                          {log.cohort_id}
                        </span>{" "}
                        • IP: {log.ip_address}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiClock} />
                        {format(new Date(log.created_at), "HH:mm:ss")}
                      </p>
                      <p className="text-[9px] text-slate-600 mt-0.5">
                        {format(new Date(log.created_at), "MMM dd, yyyy")}
                      </p>
                    </div>
                  </motion.div>
                ))}
                {auditLogs.length === 0 && (
                  <div className="text-center py-20 text-slate-600 italic">
                    Listening for registry events...
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "fido2" && (
            <div className="max-w-2xl mx-auto py-10">
              <Fido2Enrollment />
            </div>
          )}

          {activeTab === "dsar" && (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 py-20">
              <SafeIcon
                icon={FiActivity}
                className="text-6xl mb-6 opacity-20"
              />
              <h2 className="text-xl font-black uppercase italic tracking-widest mb-2">
                DSAR Suite
              </h2>
              <p className="text-sm max-w-md text-center">
                Data Subject Access Request management for GDPR/HIPAA
                compliance. Automated bundle generation for clinical cohorts.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
